import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Trans, useLingui } from '@lingui/react/macro';
import { RotateCw, UserPlus, Users } from 'lucide-react';
import { useCurrentUser } from '@/hooks/queries/auth.ts';
import { usersConsumptionQueryOptions, usersQueryOptions } from '@/hooks/queries/users.ts';
import type { APIParkUser } from '@/types/index.ts';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { PageContainer } from '@/components/page-container.tsx';
import { DataTable } from '@/components/data-table/data-table.tsx';
import type { DataTableFilterFn } from '@/components/data-table/features.ts';
import { userColumns } from './+components/user-columns.tsx';
import type { UserRow } from './+components/user-columns.tsx';
import { FastCreateDialog } from './+components/fast-create-dialog.tsx';

export const Route = createFileRoute('/_logged/users/')({
  component: UsersPage,
});

type StatusFilter = 'active' | 'expired' | 'all';

const userGlobalFilter: DataTableFilterFn<UserRow> = (row, _columnId, filterValue) => {
  const needle = String(filterValue).trim().toLowerCase();
  if (needle === '') return true;
  const user = row.original;
  return `${user.username} ${user.email}`.toLowerCase().includes(needle);
};

function toRows(
  users: Array<APIParkUser>,
  status: UserRow['status'],
  consumption: Map<number, number> | undefined,
  selfId: number,
): Array<UserRow> {
  return users.map((user) => ({
    ...user,
    consumptionKwh: consumption?.get(user.id) ?? 0,
    status,
    isSelf: user.id === selfId,
  }));
}

function UsersPage() {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();

  const [status, setStatus] = useState<StatusFilter>('active');
  const showActive = status === 'active' || status === 'all';
  const showExpired = status === 'expired' || status === 'all';

  const activeQuery = useQuery(usersQueryOptions(true));
  const expiredQuery = useQuery({ ...usersQueryOptions(false), enabled: showExpired });

  const activeUsers = activeQuery.data ?? [];
  const expiredUsers = expiredQuery.data ?? [];

  // One shared consumption query per list, subscribed here (not once per row) — a
  // per-cell query meant 2N observers on 2 cache entries and a mount-time stampede.
  const activeConsumption = useQuery(
    usersConsumptionQueryOptions(
      true,
      activeUsers.map((user) => user.id),
    ),
  );
  const expiredConsumption = useQuery({
    ...usersConsumptionQueryOptions(
      false,
      expiredUsers.map((user) => user.id),
    ),
    enabled: showExpired && expiredUsers.length > 0,
  });

  const rows: Array<UserRow> = [
    ...(showActive ? toRows(activeUsers, 'active', activeConsumption.data, currentUser.id) : []),
    ...(showExpired ?
      toRows(expiredUsers, 'expired', expiredConsumption.data, currentUser.id)
    : []),
  ];

  // Hold the whole table in its loading state until BOTH the user list and its
  // consumption have resolved, so the table renders once, fully populated. An
  // empty list needs no consumption wait.
  const activeLoading =
    showActive &&
    (activeQuery.isPending || (activeUsers.length > 0 && activeConsumption.isPending));
  const expiredLoading =
    showExpired &&
    (expiredQuery.isPending || (expiredUsers.length > 0 && expiredConsumption.isPending));
  const isLoading = activeLoading || expiredLoading;

  const isError =
    (showActive && (activeQuery.isError || activeConsumption.isError)) ||
    (showExpired && (expiredQuery.isError || expiredConsumption.isError));

  const isFetching =
    activeQuery.isFetching ||
    expiredQuery.isFetching ||
    activeConsumption.isFetching ||
    expiredConsumption.isFetching;

  function reload() {
    void activeQuery.refetch();
    if (showExpired) void expiredQuery.refetch();
    void queryClient.invalidateQueries({ queryKey: ['users-consumption'] });
  }

  const statusLabel = (value: StatusFilter) =>
    value === 'active' ? t`Active`
    : value === 'expired' ? t`Expired`
    : t({
        comment: 'Option in the Status filter dropdown meaning both active and expired users.',
        message: 'All',
      });

  return (
    // md+: the page fills the viewport below the h-12 header and the table body
    // scrolls inside its frame (fillHeight); below md the page itself scrolls.
    <PageContainer className='md:h-[calc(100svh-3rem)]'>
      <Card className='md:min-h-0 md:flex-1'>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Users className='size-5' />
            <Trans>Users</Trans>
          </CardTitle>
          <CardAction className='flex items-center gap-2'>
            <Button
              variant='ghost'
              size='sm'
              onClick={reload}
              disabled={isFetching}
              aria-label={t`Reload`}
            >
              {isFetching ?
                <Spinner />
              : <RotateCw />}
              <span className='hidden sm:inline'>
                <Trans>Reload</Trans>
              </span>
            </Button>
            <FastCreateDialog />
            <Button
              size='sm'
              nativeButton={false}
              render={<Link to='/users/create' />}
              aria-label={t`New user`}
            >
              <UserPlus />
              <span className='hidden sm:inline'>
                <Trans>New user</Trans>
              </span>
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent className='md:flex md:min-h-0 md:flex-1 md:flex-col'>
          <DataTable
            columns={userColumns}
            data={rows}
            getRowId={(user) => String(user.id)}
            isLoading={isLoading}
            isError={isError}
            onRetry={reload}
            fillHeight
            emptyMessage={<Trans>No users found.</Trans>}
            errorMessage={<Trans>Could not load users.</Trans>}
            initialSorting={[{ id: 'username', desc: false }]}
            globalFilterFn={userGlobalFilter}
            searchPlaceholder={t`Search users…`}
            toolbar={
              <Select value={status} onValueChange={(value) => setStatus(value as StatusFilter)}>
                <SelectTrigger size='sm' className='w-32' aria-label={t`Status`}>
                  <SelectValue>{(value) => statusLabel(value as StatusFilter)}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='active'>{statusLabel('active')}</SelectItem>
                  <SelectItem value='expired'>{statusLabel('expired')}</SelectItem>
                  <SelectItem value='all'>{statusLabel('all')}</SelectItem>
                </SelectContent>
              </Select>
            }
          />
        </CardContent>
      </Card>
    </PageContainer>
  );
}
